import type { Time } from '../utils';
import type { ComponentClass } from '.';
import type { QueriesObject, Query, QueryResults } from './queries';
import type { Scene } from './Scene';

/**
 * A System is a class that executes with all entities that have a certain set of components.
 * Odie uses a reactive ECS architecture using queries. These queries allow you to specify which entities you want to work with in your system
 *
 * Systems are automatically wired into the game, simply implement the abstract functions that you need.
 *
 * As a general convention any system should be named with the postfix system. eg `EnemySystem`, `TransformSystem`.
 *
 * For examples on how to create a system go to {@link http://odie.goodboydigital.com/main/}
 */
export interface System<D = any, S extends Scene = Scene> {
  /** reference to the scene the system is added too which is automatically injected */
  scene?: S;
  /** an object that contains the results of the systems queries which is automatically updated */
  queries?: QueryResults;

  /** called when the system is added to the scene. Data is passed here to initialise the system */
  init?: (data: D) => void;
  /** called when scene.start() is called, or after `system.init` if system is added after scene has started */
  awake?: () => void;
  /** called before first update */
  start?: () => void;

  /** called each frame before `system.update` */
  preupdate?: (time: Time) => void;
  /** called each frame */
  update?: (time: Time) => void;
  /** called each frame after `system.update` */
  postupdate?: (time: Time) => void;

  /** called each frame before `system.render` and after `system.postupdate` */
  prerender?: (dt: Time) => void;
  /** called each frame */
  render?: (dt: Time) => void;
  /** called each frame after `system.render` */
  postrender?: (dt: Time) => void;

  /** called when scene.reset is called, or when system is removed from scene */
  reset?: () => void;
  /** called when `scene.resize` is called, passing the new width and height */
  resize?: (w: number, h: number) => void;
  /** called when the scene is destroyed */
  destroy?: () => void;

  /** called when an entity matches one of the queries the system defines */
  addedToQuery?: (entity?: any, query?: Query) => void;
  /** called when an entity no longer matches one of the queries the system defines */
  removedFromQuery?: (entity?: any, query?: Query) => void;
  /** called when an entity that matches one of the queries the system defines has a component modified */
  modifiedQuery?: (
    entity?: any,
    changedComponent?: any,
    data?: any,
    query?: Query,
    componentClass?: ComponentClass<any, any>,
  ) => void;
}

export interface SystemClass<
  DATA = any,
  SCENE extends Scene = Scene,
  SYSTEM extends System<DATA, SCENE> = System<DATA, SCENE>,
> {
  NAME: string;
  Queries?: QueriesObject;
  new (): SYSTEM;
}

export function odieDestroySystem(system: System) {
  if (system.destroy) {
    system.destroy();
  }
  if (system.queries) {
    for (let k in system.queries) {
      system.queries[k].destroy();
    }
  }

  system.scene = undefined;
  system.queries = undefined;
}
